import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, toRefs, watch, withDefaults } from 'vue';
import type { BookV1, DecodableBookLevel, NewBookV1, Publisher } from '@ilteducation/content-registry-types';
import type { Category, Language, Serie } from '@ilteducation/content-ref-data-types';
import { BaseState } from '@ilteducation/content-registry-types/enums/content-enums';
import DisableableButton from '@/components/buttons/DisableableButton.vue';
import FormSaveButton from '@/components/base/FormSaveButton.vue';
import BookCreators from '@/components/book/BookCreators.vue';
import { createMissingContributors } from '@/comp/use-book-creators';
import ComboBox from '@/components/base/ComboBox.vue';
import ChooseSerie from '@/components/book/ChooseSerie.vue';
import ChooseAges from '@/components/book/ChooseAges.vue';
import DurationInput from '@/components/book/DurationInput.vue';
import useMediaFormFeature from '@/comp/use-media-form-feature';
import { getAllRefData, getAvailablePublishers } from '@/data-provider/refDataProvider';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import { defaultRealms, useRealmLabel } from '@/foundations/helpers/defaultRealms';
import ChooseThemas from '@/components/book/ChooseThemas.vue';
import IsbnInput from '@/components/book/IsbnInput.vue';
import RealNumberInput from '@/components/RealNumberInput.vue';
import RichTextInput from '@/components/RichTextInput.vue';
import { fetchGoogleBooksMetadata } from '@/data-provider/googleBooks';
import eventBus from '@/foundations/services/eventBus';

type Emits = {
  (e: 'bookMetaUpdated', updatedBook: NewBookV1): void;
};
type Props = {
  id?: string;
  title: string;
  item?: BookV1 | NewBookV1;
  redirect?: 'create' | 'edit';
  isCreate?: boolean;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
    id: null,
    title: null,
    item: null,
    redirect: { default: 'edit' },
    isCreate: { type: Boolean, default: false }
  },
  emits: ["bookMetaUpdated"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'bookMetaUpdated', updatedBook: NewBookV1): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { id?: string, title: string, item?: BookV1 | NewBookV1, redirect: 'create' | 'edit', isCreate: boolean }




const { id, item, isCreate, redirect } = toRefs(props);

const BOOK = ref<BookV1 | NewBookV1>({
  availabilityRealms: [],
  type: 'BookV1',
  isbn: '',
  realm: '',
  properties: {
    sentenceHighlighting: false,
    reflowable: false,
    pageProgressionDirection: 'ltr',
    pictureInPicture: [],
    additionalContent: {
      kahoot: false,
      teachingMaterial: false,
      externalMaterial: false,
    },
    frog: false,
    isFactBook: false,
    isSpeakingBook: false,
    isDecodableBook: false,
  },
  title: '',
  publisher: {
    id: '',
    name: '',
  },
  royaltyBeneficiaries: {
    id: '',
    name: '',
  },
  languages: {
    mainLanguage: '',
    text: undefined,
  },
  authors: [],
  illustrators: [],
  translators: [],
  keywords: [],
  searchableISBN: [],
  pages: 0,
  duration: 0,
  description: '',
  accessRules: {
    services: [],
    subscriptionType: [],
  },
  serie: undefined,
  themas: undefined,
});

const availablePublishers = ref([] as Publisher[]);
const availableCategories = ref([] as Category[]);
const availableLanguages = ref([] as Language[]);
const availableSeries = ref([] as Serie[]);

const shownCategories = computed(() => {
  if (item?.value?.realm || item?.value?.accessRules.subscriptionType) {
    return availableCategories.value
      .filter((cat) => cat.realms?.includes(item?.value?.realm))
      .filter((categoryInRealm) => {
        let showCat = false;
        categoryInRealm.subscriptionTypes?.forEach((catSub) => {
          BOOK.value.accessRules.subscriptionType.forEach((itemSubType) => {
            if (catSub === itemSubType) {
              showCat = true;
            }
          });
        });
        // Allow manabua native language guides to also select Inlästa categories
        if (
          BOOK.value.accessRules.services.includes('manabua') &&
          categoryInRealm.subscriptionTypes?.includes('dyslexia')
        ) {
          showCat = true;
        }
        // If the category is already selected we still want it shown
        if (BOOK.value.categories?.some((bookCategory) => bookCategory === categoryInRealm.id)) showCat = true;
        return showCat;
      });
  }
  return availableCategories;
});

getAvailablePublishers().then((publishers) => {
  availablePublishers.value = publishers.sort((a: Publisher, b: Publisher) => a.name.localeCompare(b.name));
});
getAllRefData('categories').then((categories) => {
  availableCategories.value = categories.sort((a: Category, b: Category) => a.category.localeCompare(b.category));
});
getAllRefData('languages').then((languages) => {
  availableLanguages.value = languages.sort((a: Language, b: Language) => a.language.localeCompare(b.language));
});
getAllRefData('series').then((series) => {
  availableSeries.value = series.sort((a: Serie, b: Serie) => a.name.localeCompare(b.name));
});

const customBeneficiary = ref<boolean>(
  (item?.value?.royaltyBeneficiaries?.id && item?.value.royaltyBeneficiaries?.id !== item?.value.publisher?.id) ||
    false,
);

const getRoyaltyBeneficiary = computed(() => {
  if (BOOK.value.publisher && !customBeneficiary.value) {
    const pub = BOOK.value.publisher.name ? BOOK.value.publisher : item?.value?.publisher;
    if (pub) {
      // console.log(`update RoyaltyBeneficiary '${BOOK.value.royaltyBeneficiaries.name}' => '${pub.name}'`);
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      BOOK.value.royaltyBeneficiaries = pub;
      return pub.name;
    }
  }
  return BOOK.value.royaltyBeneficiaries?.name;
});

watch([customBeneficiary], (value) => {
  if (value && BOOK.value.publisher?.id) {
    BOOK.value.royaltyBeneficiaries = {
      id: BOOK.value.publisher?.id,
      name: BOOK.value.publisher?.name,
    };
  }
});

const hasService = computed(() => (service: string) => BOOK.value.accessRules?.services.includes(service));

watch(BOOK.value.languages, (value) => {
  if (!hasService.value('polylino')) return;

  if (!value.text) {
    BOOK.value.languages.text = [value.mainLanguage];
    return;
  }
  if (value.text?.includes(value.mainLanguage) === false) {
    BOOK.value.languages.text = value.text.concat([value.mainLanguage]);
  }
});

const availableSubscriptions = computed(() =>
  hasService.value('manabua')
    ? defaultSubscriptions.filter(
        (st) => st.value.startsWith('immigrant') || BOOK.value.accessRules.subscriptionType.includes(st.value),
      )
    : defaultSubscriptions,
);
const availableBookTypes = ['book', 'audio-book', 'scrolled-book'];
const decodableLevels: DecodableBookLevel[] = ['very_easy', 'easy', 'medium', 'hard'];

const availableServices = [
  { value: 'polylino', text: 'Polylino' },
  { value: 'audio-aid', text: 'Inlästa Läromedel' },
  { value: 'manabua', text: 'Begreppa' },
];
const availableMarkets = defaultRealms;
const { realmLabel } = useRealmLabel();

const rules = {
  isbn: [(value: string) => /^[0-9a-zA-Z-]*$/.test(value) || 'ISBN should only consist of numbers, letters and dashes'],
  age: (value: number) => (value >= 0 && value <= 99) || 'Age should be between 0 and 99',
  required: (value: unknown) => !!value || 'This field is required',
  requiredMultiple: (value: unknown[]) => (Array.isArray(value) && value.length > 0) || 'This field is required',
  notEmptyRule: (val: any) => (val || '').length > 0 || 'This field is required',
  trimmedString: (val: string) =>
    !/^\s+|\s+$|\s{2,}/.test(val) || 'Leading, trailing, or multiple whitespaces in a row are not allowed',
  publishYear: (val: string) => val.length === 4 || val === '' || 'Publish year must be four digits',
};

const { saveButtonAvailable, getStateColor, toggleStateInProgress, toggleMediaState } = useMediaFormFeature(
  item?.value,
);

const previewLink = computed(() => {
  if (!id?.value || !BOOK?.value?.realm) return null;

  const isStaging = process.env.VUE_APP_ENVIRONMENT === 'staging';

  const bookId = id.value;
  const {
    realm: tld,
    accessRules: { services },
  } = BOOK.value;

  if (services.includes('audio-aid') || services.includes('manabua')) {
    return `https://inlasta${isStaging ? '-dev' : ''}.ilteducation.se/books/${bookId}`;
  }

  const realmsMapping = {
    fi: `https://app${isStaging ? '-dev' : ''}.lukulumo.${tld}/books/${bookId}`,
    fr: `https://app${isStaging ? '-dev' : ''}.sondo.${tld}/books/${bookId}`,
  };

  return realmsMapping[tld] || `https://app${isStaging ? '-dev' : ''}.polylino.${tld}/books/${bookId}`;
});

const availableMarketsWithoutRealm = computed(() => availableMarkets.filter((x) => x.code !== BOOK.value.realm));

watch(BOOK, async (newValue, oldValue) => {
  const realms = BOOK.value.availabilityRealms;
  const currentRealm = BOOK.value.realm;

  // Ensure availabilityRealms always contains the current realm or is empty if no realms are defined
  if (!realms?.length) {
    BOOK.value.availabilityRealms = currentRealm ? [currentRealm] : [];
    return;
  }

  // Handle changes to the realm
  if (newValue.realm !== oldValue.realm) {
    BOOK.value.availabilityRealms = [
      ...realms.filter((realm) => realm !== oldValue.realm && realm !== newValue.realm),
      newValue.realm,
    ];
  }

  // Handle updates to the serie and its readingOrder
  if (BOOK.value.serie && !BOOK.value.serie.readingOrder) {
    BOOK.value.serie.readingOrder = props.item?.serie?.readingOrder;
  }
});

const NATIVE_LANGUAGE_GUIDE_CATEGORY_ID =
  process.env.VUE_APP_ENVIRONMENT === 'staging' ? 'RQCTtPrNigNXJGDSV7ClM' : '7F4J48ZJtrftw967TwLRv'; // Läromedelsguide på modersmål
watch(
  () => BOOK.value.accessRules.services,
  (services) => {
    // Prefill some defaults for manabua native language guides
    if (props.isCreate && services.includes('manabua')) {
      BOOK.value.properties.sentenceHighlighting = true;
      if (!BOOK.value.categories?.includes(NATIVE_LANGUAGE_GUIDE_CATEGORY_ID)) {
        BOOK.value.categories = [...(BOOK.value.categories || []), NATIVE_LANGUAGE_GUIDE_CATEGORY_ID];
      }
      if (!BOOK.value.languages.mainLanguage) {
        BOOK.value.languages.mainLanguage = 'sv';
      }
      if (!BOOK.value.properties.bookType) {
        BOOK.value.properties.bookType = 'audio-book';
      }
      if (!('isSpeakingBook' in BOOK.value.properties)) {
        BOOK.value.properties.isSpeakingBook = false;
      }
    }
    if (props.isCreate && services.includes('audio-aid')) {
      if (!('isSpeakingBook' in BOOK.value.properties)) {
        BOOK.value.properties.isSpeakingBook = false;
      }
      if (!('isTeachingMaterial' in BOOK.value.properties)) {
        BOOK.value.properties.isTeachingMaterial = false;
      }
    }
  },
);

const isFetchingGoogleMetadata = ref(false);
const fetchingMetadata = ref(false);
const refreshKey = ref(0);

const populateMetadata = (metadata?: Partial<NewBookV1>) => {
  if (metadata) {
    // New object to trigger rerender
    BOOK.value = { ...BOOK.value, ...metadata };
    refreshKey.value += 1;
  }
  fetchingMetadata.value = false;
};

const fetchGoogleMetadata = async () => {
  const { isbn } = BOOK.value;
  if (!isbn) return;

  try {
    isFetchingGoogleMetadata.value = true;
    const metadata = await fetchGoogleBooksMetadata(isbn);
    if (!metadata) {
      eventBus.$emit('snackbar.open', `No Google Books metadata found for ISBN: ${isbn}`);
      return;
    }

    populateMetadata(metadata);
  } finally {
    isFetchingGoogleMetadata.value = false;
  }
};

const onFormSaved = () => {
  if (isCreate.value) return; // We only handle edit forms
  emits('bookMetaUpdated', BOOK.value);
};

return { __sfc: true,emits, props, id, item, isCreate, redirect, BOOK, availablePublishers, availableCategories, availableLanguages, availableSeries, shownCategories, customBeneficiary, getRoyaltyBeneficiary, hasService, availableSubscriptions, availableBookTypes, decodableLevels, availableServices, availableMarkets, realmLabel, rules, saveButtonAvailable, getStateColor, toggleStateInProgress, toggleMediaState, previewLink, availableMarketsWithoutRealm, NATIVE_LANGUAGE_GUIDE_CATEGORY_ID, isFetchingGoogleMetadata, fetchingMetadata, refreshKey, populateMetadata, fetchGoogleMetadata, onFormSaved, BaseState, DisableableButton, FormSaveButton, BookCreators, createMissingContributors, ComboBox, ChooseSerie, ChooseAges, DurationInput, ChooseThemas, IsbnInput, RealNumberInput, RichTextInput }
}

})